import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  notesApi, messagesApi, referralsApi, approvalsApi,
  goalsApi, appointmentsApi, statusUpdatesApi, escalationsApi, documentsApi,
  snapshotApi,
} from '../api/client';

/**
 * DataStore — a single global cache for all cross-role persistent collections.
 *
 * Philosophy:
 *  - Static reference data (neighbor profiles, programs, staff, households,
 *    toolkits, etc.) continues to live in /app/frontend/src/data/ mock files so
 *    no existing page regresses.
 *  - NEW persistent collections (notes, messages, referrals, approvals, goals,
 *    appointments, status_updates, escalations, documents) live here. Any role
 *    that creates an entry immediately updates the store, and every other role
 *    that reads from the store sees it — whether on the same device or a
 *    different browser/session (because the source of truth is MongoDB).
 *
 * Pages opt in by calling `useStore()` and consuming `notes`, `messages`, etc.
 * Pages that haven't migrated yet continue using the mock imports and are
 * unaffected.
 */

const COLLECTIONS = ['notes', 'messages', 'referrals', 'approvals', 'goals', 'appointments', 'statusUpdates', 'escalations', 'documents'];

const emptyState = () => COLLECTIONS.reduce((acc, k) => { acc[k] = []; return acc; }, {});

// Notify other tabs (and other components in the same tab via window-level
// BroadcastChannel) that a snapshot just changed. NeighborProfile listens
// on `district-snapshots` and bumps a refresh tick so the bento tile +
// drill-in completion % update without a full reload.
function broadcastSnapshot(neighborId, toolkitId, doc) {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel('district-snapshots');
    ch.postMessage({ kind: 'snapshot-saved', neighborId, toolkitId, doc });
    ch.close();
  } catch { /* best effort — older browsers without BC just lose the live sync */ }
}

const DataStoreContext = createContext(null);

export function DataStoreProvider({ children }) {
  const [data, setData] = useState(() => emptyState());
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    // Skip fetch when not logged in — backend now requires a Bearer token and
    // would 401 otherwise.
    const token = localStorage.getItem('district_token');
    if (!token) {
      setData(emptyState());
      setLoaded(false);
      return;
    }
    // Role-aware fetch: Neighbors only need the collections they can read
    // (scoped to own id server-side). Grants Manager has no operational
    // data scope — skip everything to avoid 403 console noise. Staff fetch everything.
    let role = null;
    try { role = (JSON.parse(localStorage.getItem('district_user') || 'null') || {}).role; } catch { /* ignore */ }
    const isNeighbor = role === 'Neighbor';
    const isGrantsManager = role === 'Grants Manager';
    if (isGrantsManager) {
      setData(emptyState());
      setLoaded(true);
      return;
    }
    setLoading(true);
    try {
      const staffOnly = (fn) => isNeighbor ? Promise.resolve([]) : fn().catch(() => []);
      const [notes, messages, referrals, approvals, goals, appointments, statusUpdates, escalations, documents] = await Promise.all([
        staffOnly(notesApi.list),
        messagesApi.list().catch(() => []),
        isNeighbor ? Promise.resolve([]) : referralsApi.list().catch(() => []),
        approvalsApi.list().catch(() => []),
        goalsApi.list().catch(() => []),
        appointmentsApi.list().catch(() => []),
        staffOnly(statusUpdatesApi.list),
        staffOnly(escalationsApi.list),
        documentsApi.list().catch(() => []),
      ]);
      setData({ notes, messages, referrals, approvals, goals, appointments, statusUpdates, escalations, documents });
      setLoaded(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('DataStore refresh failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh immediately on auth changes (login/logout) so the new user sees
  // their data without waiting for the 15s poll.
  useEffect(() => {
    const onAuth = () => refresh();
    window.addEventListener('district:auth-change', onAuth);
    return () => window.removeEventListener('district:auth-change', onAuth);
  }, [refresh]);

  // Poll every 15s so different roles/tabs see each other's changes without a hard refresh.
  useEffect(() => {
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  // Optimistic append helper
  const append = useCallback((collection, doc) => {
    setData(prev => ({ ...prev, [collection]: [doc, ...(prev[collection] || [])] }));
  }, []);

  const replace = useCallback((collection, doc) => {
    setData(prev => ({
      ...prev,
      [collection]: (prev[collection] || []).map(x => x.id === doc.id ? doc : x),
    }));
  }, []);

  const actions = useMemo(() => ({
    // Notes
    addNote: async (payload) => { const d = await notesApi.create(payload); append('notes', d); return d; },
    // Messages
    sendMessage: async (payload) => { const d = await messagesApi.create(payload); append('messages', d); return d; },
    markMessageRead: async (id) => { await messagesApi.markRead(id); setData(prev => ({ ...prev, messages: prev.messages.map(m => m.id === id ? { ...m, read: true } : m) })); },
    // Referrals
    addReferral: async (payload) => { const d = await referralsApi.create(payload); append('referrals', d); return d; },
    updateReferral: async (id, patch) => { const d = await referralsApi.update(id, patch); replace('referrals', d); return d; },
    // Approvals
    requestApproval: async (payload) => { const d = await approvalsApi.create(payload); append('approvals', d); return d; },
    decideApproval: async (id, patch) => { const d = await approvalsApi.update(id, patch); replace('approvals', d); return d; },
    // Goals
    addGoal: async (payload) => { const d = await goalsApi.create(payload); append('goals', d); return d; },
    updateGoal: async (id, patch) => { const d = await goalsApi.update(id, patch); replace('goals', d); return d; },
    // Appointments
    addAppointment: async (payload) => { const d = await appointmentsApi.create(payload); append('appointments', d); return d; },
    updateAppointment: async (id, patch) => { const d = await appointmentsApi.update(id, patch); replace('appointments', d); return d; },
    // Status
    recordStatusUpdate: async (payload) => { const d = await statusUpdatesApi.create(payload); append('statusUpdates', d); return d; },
    // Escalations
    addEscalation: async (payload) => { const d = await escalationsApi.create(payload); append('escalations', d); return d; },
    updateEscalation: async (id, patch) => { const d = await escalationsApi.update(id, patch); replace('escalations', d); return d; },
    // Documents
    addDocument: async (payload) => { const d = await documentsApi.create(payload); append('documents', d); return d; },
    // Snapshot responses (Personal Snapshot, Financial Health, etc.). Field
    // values from CM Focus Mode `/intake` and `/snapshot` slash commands
    // upsert here so Profile 360° + PM dashboards see what's been filled.
    // Broadcasts to other tabs via BroadcastChannel so a CM with the
    // neighbor's profile open in a second window sees the % tick up live.
    saveSnapshotField: async (neighborId, toolkitId, fieldKey, value, meta = {}) => {
      const d = await snapshotApi.patch(neighborId, toolkitId, { responses: { [fieldKey]: value }, ...meta });
      broadcastSnapshot(neighborId, toolkitId, d);
      return d;
    },
    saveSnapshotResponses: async (neighborId, toolkitId, responses, meta = {}) => {
      const d = await snapshotApi.patch(neighborId, toolkitId, { responses, ...meta });
      broadcastSnapshot(neighborId, toolkitId, d);
      return d;
    },
    // Refetch
    refresh,
  }), [append, replace, refresh]);

  // Handy selectors
  const selectors = useMemo(() => ({
    notesForNeighbor: (nid) => data.notes.filter(n => n.neighborId === nid),
    messagesForNeighbor: (nid) => data.messages.filter(m => m.neighborId === nid),
    referralsForNeighbor: (nid) => data.referrals.filter(r => r.neighborId === nid),
    approvalsForNeighbor: (nid) => data.approvals.filter(a => a.neighborId === nid),
    goalsForNeighbor: (nid) => data.goals.filter(g => g.neighborId === nid),
    appointmentsForNeighbor: (nid) => data.appointments.filter(a => a.neighborId === nid),
    appointmentsForStaff: (sid) => data.appointments.filter(a => a.staffId === sid),
    escalationsForRole: (role) => data.escalations.filter(e => e.toRole === role && e.status !== 'resolved'),
    pendingApprovals: () => data.approvals.filter(a => a.status === 'pending'),
    pendingReferrals: (role) => data.referrals.filter(r => r.status === 'pending' && (!role || r.toRole === role)),
  }), [data]);

  const value = useMemo(() => ({ ...data, loaded, loading, ...actions, ...selectors }), [data, loaded, loading, actions, selectors]);

  return <DataStoreContext.Provider value={value}>{children}</DataStoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(DataStoreContext);
  if (!ctx) throw new Error('useStore must be used within DataStoreProvider');
  return ctx;
}
